import { RichText } from '@sitecore-jss/sitecore-jss-react';
import React, { FC, useCallback, useState } from 'react';
import { getImageField } from '../../../../../utils/getImageFields';
import { getThumbnailProps } from '../../../../../utils/youtube.utils';
import { CustomText } from '../../../../@shared/customText';
import Icon from '../../../../@shared/icon';
import ModalDialog from '../../../../@shared/modalDialog';
import OverlayCarouselPopup from '../../../../@shared/overlayCarouselPopup';
import Picture from '../../../../@shared/picture';
import { ColumnLayoutEnum, TImageCardProps } from '../../../types/cardColumnLayout.types';
import CardColumnButtons from '../cardButtons';
import './styles.scss';
import SplitModuleContainer from '../../../../@shared/splitModuleContainer';

const ImageCard: FC<TImageCardProps> = ({ model, layout, textcolor, primarycta, secondarycta, cardHeadingTag }) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showSplitModule, setShowSplitModule] = useState(false);

  const { thumbnailImgSrc } = getThumbnailProps(model?.video?.value?.href || '');
  const closeOverlayModal = (): void => setShowOverlay(false);
  const handleSplitModuleModal = (state: boolean): void => setShowSplitModule(state);

  const showOverlayVideo = (): void => {
    if (!model?.useVideo) return;
    setShowOverlay(true);
  };

  const imageOrThumbnail = getImageField(model?.image, thumbnailImgSrc || '', model?.useVideo);
  const mobileImageOrThumbnail = getImageField(model?.mobileImage, thumbnailImgSrc || '', model?.useVideo);

  const getOverlayProps = useCallback(() => {
    return [
      {
        desktopImage: imageOrThumbnail,
        mobileImage: mobileImageOrThumbnail,
        fullImage: imageOrThumbnail,
        desktopAlt: `${imageOrThumbnail?.value?.alt}`,
        mobileAlt: `${mobileImageOrThumbnail?.value?.alt}`,
        isVideo: model?.useVideo,
        videoUrl: model?.video?.value?.href,
      },
    ];
  }, [imageOrThumbnail, mobileImageOrThumbnail, model?.useVideo, model?.video]);

  return (
    <>
      {(imageOrThumbnail?.value?.src || mobileImageOrThumbnail?.value?.src || model?.useVideo) && (
        <div className="card-column__image-shadow--wrapper">
          <div className="card-column__image-wrapper-div">
            <Picture
              editorialImageProps={{
                desktopImage: imageOrThumbnail,
                mobileImage: mobileImageOrThumbnail,
                desktopTransformation: layout === ColumnLayoutEnum.FourColumnLayout ? 'Size500' : 'Size1000',
                mobileTransformation: 'Size500',
                isVideoThumbnail: model?.useVideo,
                cssClassPictureEl: `card-column__image-wrapper card-column__image-wrapper--${layout}`,
              }}
            />
            {model?.useVideo && (
              <div onClick={showOverlayVideo} className="card-column__video-play-button">
                <Icon iconName="action-play" size={32} color="white" />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="card-column__content-placeholder">
        <div className="card-column__content-wrapper">
          <div className="card-column__text-wrapper">
            <div className="card-column__text-wrapper--body">
              {model?.title && (
                <CustomText
                  tag={cardHeadingTag}
                  field={model?.title}
                  className={`card-column__title card-column-layout__text-color-${textcolor}`}
                />
              )}
              {model?.subheading && (
                <RichText
                  tag="div"
                  field={model?.subheading}
                  className={`card-column__description card-column-layout__text-color-${textcolor} rich-text color-inherit-link`}
                />
              )}
            </div>
            <CardColumnButtons
              cta1={model?.cta1}
              cta2={model?.cta2}
              primaryctaColor={primarycta}
              secondaryctaColor={secondarycta}
              validcta2Exists={model?.validcta2Exists}
              downloadButtonLink={model?.downloadButtonLink}
              validDownloadExists={model?.validDownloadExists}
              handleSplitModuleModal={handleSplitModuleModal}
              showPopupWithSplitModule={model.showPopupWithSplitModule}
              splitModuleLabel={model.splitModuleLabel}
            />
          </div>
        </div>
      </div>

      {model?.video && model?.useVideo && (
        <ModalDialog isOpen={showOverlay} close={closeOverlayModal}>
          <OverlayCarouselPopup slides={getOverlayProps()} />
        </ModalDialog>
      )}

      {model?.showPopupWithSplitModule && model?.splitModules && (
        <ModalDialog isOpen={showSplitModule} close={(): void => handleSplitModuleModal(false)} className="split-module-modal">
          {model.splitModules.map((splitModule, index) => (
            <SplitModuleContainer {...splitModule} key={`split-Module-${index}`} params={{ imagePosition: index % 2 == 0 ? 'right' : 'left' }} />
          ))}
        </ModalDialog>
      )}
    </>
  );
};

export default ImageCard;
